// scanDiagnostics.js
//
// READ-ONLY analysis of what a scan will ask the audio engine to do.
//
// Nothing here plays, schedules, or alters a single note. It walks the same
// region and loudness code that playback uses, does arithmetic, and returns
// numbers. Import it, call it, print the result — the audio path is untouched.
//
// It answers the two questions the app cannot currently answer:
//
//   1. "Why is this stretch silent?"      → inspectColumn()
//   2. "Where will it stutter, and why?"  → analyzeScan()
//
// WHY VOICES PILE UP
// ------------------
// smplr rings each voice on for `ampRelease` (default 0.3 s) PAST its note-off.
// A region's note only occupies its own slot — colMs / regions — but it SOUNDS
// for slot + 0.3 s. So concurrent voices go roughly as
//
//     voices  ≈  0.3 s  x  (regions per second)  x  (notes per region)
//
// which means image detail and tempo are the SAME VARIABLE: doubling the regions
// in a column is identical to halving the column's duration. A busy column at
// Largo is cheap; a simple one at Presto may not be. That is why a warning about
// "complex images" would be only half true — and why measuring beats guessing.

// These mirror the engine (fractalMusic.js). They are read here, never written
// there. If you change CAP or the Soundfont decayTime, change them here too or
// the meter will quietly lie to you.
import { VOICE_CAP } from "./fractalMusic";  // single source of truth for the voice cap
export const SMPLR_RELEASE_SEC = 0.3;  // smplr PARAM_DEFAULTS.ampRelease
export { VOICE_CAP };                        // re-export so App.js keeps importing it from here
const GAP_FACTOR = 0.9;                // startPlayback: (regionMs / 1000) * 0.9

/**
 * How many notes a region actually fires — including the engine's cap, so the
 * meter counts what really sounds rather than a worst case that would cry wolf.
 */
function notesForRegion(region, lookup) {
  if (region.inSet) return [];
  const notes = lookup(region.escape) || [];
  if (notes.length <= VOICE_CAP) return notes;
  return notes.slice().sort((a, b) => b.velocity - a.velocity).slice(0, VOICE_CAP);
}

/**
 * Walk the whole scan and report the voice load, column by column.
 *
 * @param {object}   o
 * @param {number}   o.scanExtent      WIDTH (vertical scan) or HEIGHT (horizontal)
 * @param {string}   o.orientation
 * @param {function} o.buildRegions    (col, orientation) => regions   [buildInstrumentalRegions]
 * @param {function} o.tempoForColumn  (col) => ms                     [the movements tempo map]
 * @param {function} o.lookup          (escape) => notes[]             [getLoudnessLookup(), HOISTED —
 *                                     it sorts the whole escape array, so never call it per region]
 * @param {Array}    o.movements       for end-of-movement rests
 *
 * @returns {{
 *   columns: Array<{col, regions, inSet, notes, peakVoices, silent}>,
 *   peakVoices: number, peakCol: number,
 *   hottest: Array, silentRuns: Array<{from, to}>,
 *   totalSec: number
 * }}
 */
export function analyzeScan({
  scanExtent,
  orientation,
  buildRegions,
  tempoForColumn,
  lookup,
  movements = [],
}) {
  const cols = [];
  const marks = [];   // [time, delta] — note-on / note-off boundaries
  let t = 0;

  for (let col = 0; col < scanExtent; col++) {
    const colSec = tempoForColumn(col) / 1000;
    const colStart = t;

    const regions = buildRegions(col, orientation) || [];
    const totalHeight = regions.reduce((s, r) => s + r.height, 0) || 1;

    let inSet = 0, noteCount = 0, acc = 0;
    for (const region of regions) {
      const regionSec = (region.height / totalHeight) * colSec;
      if (region.inSet) {
        inSet++;
      } else {
        const n = notesForRegion(region, lookup).length;
        if (n > 0) {
          const start = t + acc;
          // The note sounds for its slot AND its release tail — that tail is what
          // makes voices from different regions (and different columns) overlap.
          const end = start + regionSec * GAP_FACTOR + SMPLR_RELEASE_SEC;
          marks.push([start, n], [end, -n]);
          noteCount += n;
        }
      }
      acc += regionSec;
    }

    t += colSec;
    const mv = movements.find((m) => m.endCol === col && (m.endRestBeats || 0) > 0);
    if (mv) t += mv.endRestBeats * colSec;

    cols.push({
      col,
      start: colStart,
      end: t,
      regions: regions.length,
      inSet,
      notes: noteCount,
      peakVoices: 0,
      silent: noteCount === 0,
    });
  }

  // Sweep the note-on/note-off boundaries to get concurrent voices over time,
  // then attribute the peak within each column's time window. Release tails mean
  // a column can inherit voices from the column before it — which is exactly the
  // effect we are trying to measure, so the sweep is global, not per-column.
  marks.sort((a, b) => a[0] - b[0]);
  let cur = 0, peak = 0, peakAt = 0, ci = 0;
  for (const [time, delta] of marks) {
    while (ci < cols.length && cols[ci].end <= time) {
      if (cur > cols[ci].peakVoices) cols[ci].peakVoices = cur;
      ci++;
    }
    if (ci < cols.length && cur > cols[ci].peakVoices) cols[ci].peakVoices = cur;
    cur += delta;
    if (ci < cols.length && time >= cols[ci].start && time < cols[ci].end) {
      if (cur > cols[ci].peakVoices) cols[ci].peakVoices = cur;
    }
    if (cur > peak) { peak = cur; peakAt = time; }
  }
  while (ci < cols.length) {
    if (cur > cols[ci].peakVoices) cols[ci].peakVoices = cur;
    ci++;
  }

  const peakColEntry = cols.find((c) => peakAt >= c.start && peakAt < c.end);

  // Silent stretches, collapsed into runs — a run of silent columns is a musical
  // event (a rest); a scatter of them is usually a palette floor showing through.
  const silentRuns = [];
  let run = null;
  for (const c of cols) {
    if (c.silent) {
      if (!run) run = { from: c.col, to: c.col };
      else run.to = c.col;
    } else if (run) {
      silentRuns.push(run);
      run = null;
    }
  }
  if (run) silentRuns.push(run);

  const hottest = cols
    .slice()
    .sort((a, b) => b.peakVoices - a.peakVoices)
    .slice(0, 8);

  return {
    columns: cols,
    peakVoices: peak,
    peakCol: peakColEntry ? peakColEntry.col : -1,
    hottest,
    silentRuns,
    totalSec: t,
  };
}

/**
 * Everything about one column: each region, whether it is in the set, its escape
 * value, the band that escape maps to, and exactly which instruments fire at what
 * velocity. This is what answers "why is this silent?" — a region with an escape
 * value but zero notes means every instrument in the palette computes a loudness
 * of zero at that band, i.e. the bloom has not switched anyone on yet.
 */
export function inspectColumn({
  col,
  orientation,
  buildRegions,
  tempoForColumn,
  lookup,
  bandFor,          // (escape) => 0..255, or null if unavailable
}) {
  const regions = buildRegions(col, orientation) || [];
  const totalHeight = regions.reduce((s, r) => s + r.height, 0) || 1;
  const colSec = tempoForColumn(col) / 1000;

  const rows = regions.map((region) => {
    const regionSec = (region.height / totalHeight) * colSec;
    const notes = notesForRegion(region, lookup);
    const raw = region.inSet ? [] : (lookup(region.escape) || []);
    return {
      height: region.height,
      slotMs: Math.round(regionSec * 1000),
      inSet: !!region.inSet,
      escape: region.escape,
      band: region.inSet || !bandFor ? null : bandFor(region.escape),
      notes,                        // what actually sounds (after the cap)
      capped: raw.length > notes.length ? raw.length - notes.length : 0,
    };
  });

  const voices = rows.reduce((s, r) => s + r.notes.length, 0);
  return { col, colMs: Math.round(colSec * 1000), rows, voices };
}
